<template>
  <Teleport to="body">
    <div
      v-if="props.keepMounted || modelValue"
      v-show="modelValue"
      :class="$style.backdrop"
      :style="backdropInlineStyle"
      @click="onBackdropClick"
    >
      <div
        ref="modalRef"
        :class="$style.modal"
        :style="modalStyle"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        @keydown="onKeydown"
        @click.stop
      >
        <header
          v-if="showTitle"
          :class="[
            $style.header,
            props.showClose ? $style.headerWithClose : $style.headerNoClose,
            !props.showTitleIcon && props.showClose ? $style.headerWithCloseNoIcon : null,
          ]"
        >
          <div :class="$style.headerContent">
            <img v-if="showTitleIcon" :src="titleIconSrc" :class="$style.titleIcon" alt="" />
            <span :class="$style.title">{{ title }}</span>
          </div>
        </header>

        <button
          v-if="showClose"
          type="button"
          :class="$style.closeButton"
          aria-label="閉じる"
          @click="close"
        >
          <img :src="closeIconSrc" :class="$style.closeIcon" alt="" />
        </button>

        <section
          :class="[
            $style.body,
            showTitle ? $style.bodyWithHeader : $style.bodyPlain,
            { [$style.bodyNoPadding]: props.disableBodyPadding },
            { [$style.bodyEmbedsSlotScroll]: props.bodyEmbedsSlotScroll },
          ]"
          :style="bodyPaddingStyle"
        >
          <slot />
        </section>

        <footer v-if="$slots.footer" :class="[$style.footer, footerClass]">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import closeGray from '@/assets/images/icons/modal-close-gray.svg';
import closeWhite from '@/assets/images/icons/modal-close-white.svg';
import agentLogo from '@/assets/images/platform-logo-agent.webp';
import directLogo from '@/assets/images/platform-logo-direct.webp';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import type { ModalEmits, ModalProps, ScrollLockState } from './types';

const props = withDefaults(defineProps<ModalProps>(), {
  modelValue: false,
  title: '',
  showTitle: true,
  showTitleIcon: false,
  titleIconType: 'agent',
  showClose: true,
  closeOnBackdrop: true,
  closeOnEsc: true,
  maxWidth: 640,
  disableBodyPadding: false,
  bodyEmbedsSlotScroll: false,
  autoFocus: true,
  bodyPadding: undefined,
  footerClass: undefined,
  keepMounted: false,
  backdropZIndexVar: undefined,
});

const emits = defineEmits<ModalEmits>();

const modalRef = ref<HTMLElement | null>(null);
const lastActiveElement = ref<HTMLElement | null>(null);
const isScrollLocked = ref(false);

const getScrollLockState = (): ScrollLockState => {
  const globalKey = '__platformModalScrollLock__';
  const globalObj = globalThis as typeof globalThis & {
    __platformModalScrollLock__?: ScrollLockState;
  };

  if (!globalObj[globalKey]) {
    globalObj[globalKey] = { count: 0, previousOverflow: '' };
  }

  return globalObj[globalKey] as ScrollLockState;
};

const scrollLockState = getScrollLockState();

const titleIconSrc = computed(() => (props.titleIconType === 'agent' ? agentLogo : directLogo));

const closeIconSrc = computed(() => (props.showTitle ? closeWhite : closeGray));

const modalStyle = computed(() => ({
  maxWidth: `${props.maxWidth}px`,
  '--modal-max-width': `${props.maxWidth}px`,
}));

const backdropInlineStyle = computed<Record<string, string>>(() => {
  if (props.backdropZIndexVar) {
    return { zIndex: `var(${props.backdropZIndexVar})` };
  }
  if (props.zIndex === undefined || props.zIndex === null) return {};
  return { zIndex: String(props.zIndex) };
});

const bodyPaddingStyle = computed(() => {
  if (props.disableBodyPadding) return {};
  if (props.bodyPadding !== undefined) {
    return { padding: props.bodyPadding };
  }
  return !props.showTitle && props.showClose ? { paddingTop: '56px' } : {};
});

const close = (): void => {
  emits('update:modelValue', false);
  emits('close');
};

const onBackdropClick = (): void => {
  if (props.closeOnBackdrop !== false) {
    close();
  }
};

const lockScroll = (): void => {
  if (isScrollLocked.value) return;

  if (scrollLockState.count === 0) {
    scrollLockState.previousOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
  }

  scrollLockState.count += 1;
  isScrollLocked.value = true;
};

const unlockScroll = (): void => {
  if (!isScrollLocked.value) return;

  if (scrollLockState.count > 0) {
    scrollLockState.count -= 1;
  }

  if (scrollLockState.count === 0) {
    document.body.style.overflow = scrollLockState.previousOverflow;
  }

  isScrollLocked.value = false;
};

const focusableSelector =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getFocusableElements = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
  );

const restoreFocus = (): void => {
  if (lastActiveElement.value && typeof lastActiveElement.value.focus === 'function') {
    lastActiveElement.value.focus();
  }
  lastActiveElement.value = null;
};

const focusFirstElement = async (): Promise<void> => {
  await nextTick();
  const root = modalRef.value;
  if (!root) return;
  root.focus();
};

const handleTabKey = (event: KeyboardEvent): void => {
  const root = modalRef.value;
  if (!root) return;

  const focusableElements = getFocusableElements(root);

  if (focusableElements.length === 0) {
    event.preventDefault();
    root.focus();
    return;
  }

  const first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];
  const activeElement = document.activeElement as HTMLElement | null;

  if (event.shiftKey) {
    if (activeElement === first || !root.contains(activeElement)) {
      event.preventDefault();
      last.focus();
    }
  } else if (activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const onKeydown = (event: KeyboardEvent): void => {
  if (!props.modelValue) return;

  if (event.key === 'Escape' && props.closeOnEsc !== false) {
    event.preventDefault();
    close();
    return;
  }

  if (event.key === 'Tab') {
    handleTabKey(event);
  }
};

const applyOpenState = (): void => {
  lockScroll();
  lastActiveElement.value =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  if (props.autoFocus !== false) {
    void focusFirstElement();
  }
};

const applyCloseState = (): void => {
  restoreFocus();
  unlockScroll();
};

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      applyOpenState();
    } else {
      applyCloseState();
    }
  }
);

if (props.modelValue) {
  applyOpenState();
}

onBeforeUnmount(() => {
  applyCloseState();
});
</script>

<style module lang="scss">
@import '@/assets/stylesheets/components/_variables.scss';

.backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 330px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  z-index: $z-overlay;
}

.modal {
  position: relative;
  width: min(var(--modal-max-width, 640px), calc(100% - 32px));
  max-height: 90vh;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}

.header {
  background: #005d9a;
  color: #fff;
  min-height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 56px;
  text-align: center;
}

.headerWithClose {
  padding: 20px 56px;
}

.headerNoClose {
  padding: 20px 56px;
}

.headerContent {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.titleIcon {
  width: 60px;
  height: 60px;
  border-radius: 999px;
  object-fit: cover;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1);
}

.title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.55px;
  line-height: 20px;
  color: #fff;
}

.closeButton {
  position: absolute;
  top: 15px;
  right: 15px;
  border: none;
  background: transparent;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.closeIcon {
  width: 18px;
  height: 18px;
}

.body {
  color: #000;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.55px;
  padding: 24px;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.bodyWithHeader {
  background: #fff;
}

.bodyNoPadding {
  padding: 0;
}

.bodyEmbedsSlotScroll {
  overflow-y: hidden;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.footer {
  padding: 24px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
}

@media (max-width: 1080px) {
  .backdrop {
    padding: 0;
  }

  .modal {
    width: calc(100% - 16px);
  }
  .header {
    padding: 16px;
  }
  .headerWithClose {
    padding: 16px 48px 16px 16px;
  }
  .headerWithCloseNoIcon {
    padding: 16px 48px;
  }
  .headerNoClose {
    padding: 16px;
  }
  .title {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.55px;
    line-height: 20px;
    color: #fff;
  }
}
</style>
